/**
 * Amanarea unei plati — feature-ul care nu exista deloc in fisier.
 *
 * Invariantul care conduce tot: **scadenta (`dueDate`) NU se atinge niciodata**. Amanarea muta
 * plata in ALTA runda, in alta zi, si creeaza un rand nou legat de cel vechi; randul de plecare
 * ramane vizibil in sectiunea `Amânate azi`. De aceea „cu cate zile lucratoare am intarziat fata de
 * scadenta" e o cifra reala si nu o poveste — si de aceea hookul NU face actualizare optimista:
 * randul nou are un id pe care doar serverul il stie, iar o linie inventata in grila ar fi o plata
 * care nu exista.
 *
 * Invalidarea e larga si deliberata: o amanare atinge DOUA zile (cea de plecare si cea de sosire),
 * matricea, cashflowul si lantul. Un `invalidateQueries` pe prefixul `zi` costa un refetch de 12-33
 * de randuri; o zi ramasa nesincronizata costa o plata care pare programata de doua ori.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { PostponeHop } from '@evomag/core/postpone';

import { api, type CuImplicite } from '../lib/api.ts';
import { chei } from '../lib/query-keys.ts';
import type { PostponeInput, PostponeResponse } from '../../../../functions/api/schemas.ts';

export type { PostponeResponse };

// ---------------------------------------------------------------------------
// POST /api/entries/:id/postpone
// ---------------------------------------------------------------------------

export type IntrareAmanare = CuImplicite<PostponeInput, 'mode'>;

export interface VariabileAmanare {
  entryId: number;
  /** Ziua din care pleaca plata; se foloseste doar pentru invalidare. */
  dinData: string;
  amanare: IntrareAmanare;
}

export function fnAmanare(variabile: VariabileAmanare): Promise<PostponeResponse> {
  return api.post<PostponeResponse>(`/entries/${variabile.entryId}/postpone`, variabile.amanare);
}

export function useAmanare(): UseMutationResult<PostponeResponse, Error, VariabileAmanare> {
  const queryClient = useQueryClient();
  return useMutation<PostponeResponse, Error, VariabileAmanare>({
    mutationKey: chei.mutatii.amanare(),
    mutationFn: fnAmanare,
    onSuccess: (_raspuns, variabile) => {
      // Prefixul `zi`, nu ziua de plecare: randul nou aterizeaza in ALTA zi, care poate fi deschisa
      // intr-un alt tab sau in coloana urmatoare a matricei.
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.cashflow.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.amanare.lant(variabile.entryId) });
      void queryClient.invalidateQueries({ queryKey: chei.amanare.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// GET /api/entries/:id/chain
// ---------------------------------------------------------------------------

export interface RezumatLant {
  hopCount: number;
  firstDate: string | null;
  currentDate: string | null;
  /** Cifra care azi nu exista nicaieri in Excel. */
  totalBusinessDaysDelayed: number;
  automaticHops: number;
  reasons: string[];
}

export interface RaspunsLantAmanari {
  entryId: number;
  dueDate: string | null;
  /** `false` = invariantul s-a rupt undeva pe lant si trebuie sa se vada, nu sa se ascunda. */
  dueDateStable: boolean;
  hops: PostponeHop[];
  summary: RezumatLant;
}

export function useLantAmanari(
  entryId: number | null | undefined,
): UseQueryResult<RaspunsLantAmanari, Error> {
  return useQuery({
    queryKey: chei.amanare.lant(entryId ?? 0),
    queryFn: ({ signal }) =>
      api.get<RaspunsLantAmanari>(`/entries/${entryId ?? 0}/chain`, { semnal: signal }),
    // Lantul se deschide din drawer: nu se cere cat timp nu e nimic deschis.
    enabled: typeof entryId === 'number' && entryId > 0,
  });
}

// ---------------------------------------------------------------------------
// GET /api/postpone/preview
// ---------------------------------------------------------------------------

export interface CandidatAmanare {
  entryId: number;
  supplierName: string;
  amountRonMinor: number;
  status: string;
  mode: string;
  targetDate: string | null;
  hopIndex: number;
  businessDaysDelayed: number;
  businessDaysPastDue: number | null;
  willPostpone: boolean;
  problems: string[];
  warnings: string[];
}

export interface RaspunsPreviewAmanare {
  date: string;
  autoEnabled: boolean;
  maxHops: number;
  defaultPolicy: string;
  candidateCount: number;
  willPostponeCount: number;
  blockedCount: number;
  totalAmountRonMinor: number;
  /** Starile pe care matura de seara NU le atinge, ca sa se poata explica in interfata. */
  ineligibleStatuses: Array<{ code: string; labelRo: string }>;
  entries: CandidatAmanare[];
}

/**
 * Ce ar face matura de la 18:00, INAINTE sa o faca.
 *
 * Automatizarea silentioasa intr-un instrument de trezorerie distruge increderea o data si
 * definitiv; previzualizarea e pretul pentru a avea voie sa automatizezi ceva.
 */
export function usePreviewAmanare(
  data: string,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsPreviewAmanare, Error> {
  return useQuery({
    queryKey: chei.amanare.previzualizare(data),
    queryFn: ({ signal }) =>
      api.get<RaspunsPreviewAmanare>('/postpone/preview', {
        interogare: { date: data },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false && data !== '',
    // Previzualizarea depinde de starile curente ale randurilor: nu are sens sa fie tinuta mult.
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// PUT /api/postpone/auto
// ---------------------------------------------------------------------------

export interface RaspunsComutatorAmanare {
  key: string;
  enabled: boolean;
}

export function fnComutaAmanareAuto(variabile: {
  enabled: boolean;
}): Promise<RaspunsComutatorAmanare> {
  return api.put<RaspunsComutatorAmanare>('/postpone/auto', variabile);
}

/** Comutatorul global al maturii de seara. Rol `admin`: e o decizie de politica, nu o preferinta. */
export function useComutaAmanareAuto(): UseMutationResult<
  RaspunsComutatorAmanare,
  Error,
  { enabled: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsComutatorAmanare, Error, { enabled: boolean }>({
    mutationKey: chei.mutatii.amanareAuto(),
    mutationFn: fnComutaAmanareAuto,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.amanare.tot() });
      // `postpone.auto_enabled` face parte din `settings` din payloadul de bootstrap.
      void queryClient.invalidateQueries({ queryKey: chei.referinta.tot() });
    },
  });
}
